"use client"

import {
  Button,
  ButtonGroup,
  Text,
  VStack,
  Box,
  Table,
  Icon,
} from "@chakra-ui/react"
import { useState } from "react"
import Papa from "papaparse"
import * as XLSX from "xlsx"
import { FaUpload } from "react-icons/fa"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { TableFieldsService, TableRowsService } from "../../client"
import useCustomToast from "../../hooks/useCustomToast"
import {ProgressBar} from "@/components/ui/progress-bar"

import {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
  DialogActionTrigger,
} from "../ui/dialog"

import {
  FileUploadDropzone,
  FileUploadList,
  FileUploadRoot,
} from "@/components/ui/file-upload"

interface BulkUploadTableRowsProps {
  tableId: string
  disabled?: boolean
}

const BulkUploadTableRows = ({ tableId, disabled = false }: BulkUploadTableRowsProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [parsedData, setParsedData] = useState<any[]>([])
  const [uploading, setUploading] = useState(false)

  const [progress, setProgress] = useState(0) // 0 to 100

  const { showSuccessToast, showErrorToast } = useCustomToast()
  const queryClient = useQueryClient()

  const { data: fieldData } = useQuery({
    queryKey: ["tableFields", tableId],
    queryFn: () => TableFieldsService.readTableFields({ tableId }),
    enabled: !!tableId,
  })

  const fields = fieldData?.data || []
  const fieldMappings = fields
  .filter((f) => f.field_code && f.field_name)
  .map((f) => ({ code: f.field_code?.trim(), name: f.field_name.trim() }))

  const handleSelectedFile = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase()
    const handleParsed = (rawRows: any[]) => {
      if (!fieldData?.data) return
    
      const codeToNameMap: Record<string, string> = {}
      for (const f of fieldData.data) {
        if (f.field_code && f.field_name) {
          codeToNameMap[f.field_code] = f.field_name
        }
      }
    
      const transformedRows = rawRows.map((row) => {
        const transformed: Record<string, any> = {}
        for (const code in row) {
          const fieldName = codeToNameMap[code]
          if (fieldName) {
            transformed[fieldName] = row[code]
          }
        }
        return transformed
      })
    
      setParsedData(transformedRows)
    }
  
    if (ext === "csv") {
      const reader = new FileReader()
      reader.onload = () => {
        let text = reader.result as string
        if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)
        const result = Papa.parse(text, { header: true, skipEmptyLines: true })
  
        if (result.errors.length > 0) {
          console.error("CSV parse error:", result.errors)
          showErrorToast("CSV 파일 파싱 중 오류가 발생했습니다.")
          return
        }
  
        handleParsed(result.data)
      }
      reader.readAsText(file, "utf-8")
    } else if (ext === "xlsx") {
      const reader = new FileReader()
      reader.onload = (event) => {
        const workbook = XLSX.read(event.target?.result, { type: "binary" })
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const json = XLSX.utils.sheet_to_json(worksheet)
        handleParsed(json)
      }
      reader.readAsBinaryString(file)
    } else {
      showErrorToast("CSV 또는 Excel 파일만 업로드할 수 있습니다.")
    }
  }
  

  const handleUpload = async () => {
    if (!parsedData.length || uploading) return
    setUploading(true)
    setProgress(0)
  
    try {
      for (let i = 0; i < parsedData.length; i++) {
        await TableRowsService.createTableRow({
          tableId,
          requestBody: { data: parsedData[i] },
        })
        setProgress(Math.round(((i + 1) / parsedData.length) * 100))
      }
  
      showSuccessToast(`${parsedData.length}개의 행이 제출되었습니다.`)
    } catch (err) {
      showErrorToast("일부 행 업로드 중 오류가 발생했습니다.")
      console.error(err)
    } finally {
      setParsedData([])
      setIsOpen(false)
      setUploading(false)
      setProgress(0)
  
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "openSearchDocs" &&
          typeof query.queryKey[1] === "object" &&
          (query.queryKey[1] as { tableId: string }).tableId === tableId,
      })
    }
  }
  
  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0]
  //   if (file) handleSelectedFile(file)
  // }  

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button my={4} disabled={disabled}>
          <Icon as={FaUpload}  fontSize="16px" mr={1} />
            대량 등록
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>대량 등록</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="start" gap={4}>
            <Text fontSize="sm">CSV 또는 Excel 파일을 업로드하여 여러 행을 추가할 수 있습니다.</Text>

            <FileUploadRoot
              maxW="xl"
              alignItems="stretch"
              maxFiles={1}
              inputProps={{
                accept: ".csv,.xlsx",
                onChange: (e) => {
                  const file = e.target.files?.[0]
                  if (file) handleSelectedFile(file)
                },
              }}
            >
              <FileUploadDropzone
                label="CSV 또는 Excel 파일을 드래그하거나 클릭해서 업로드하세요"
                description="지원되는 형식: .csv, .xlsx"
              />
              <FileUploadList showSize={true} />
            </FileUploadRoot>



            {parsedData.length > 0 && fieldMappings.length > 0 && (
              <>
                <Text fontSize="sm" color="gray.600">
                  {parsedData.length}개의 행이 분석되었습니다.
                </Text>

                <Box maxH="250px" overflowY="auto" w="full" borderWidth="1px" borderRadius="md">
                  <Table.Root size={{ base: "sm", md: "md" }}>
                    <Table.Header>
                      <Table.Row>
                        {fieldMappings.map(({ code, name }) => (
                          <Table.ColumnHeader key={code}>
                            {code} ({name})
                          </Table.ColumnHeader>
                        ))}
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {parsedData.slice(0, 10).map((row, idx) => (
                        <Table.Row key={idx}>
                          {fieldMappings.map(({ name }) => (
                            <Table.Cell key={`${idx}-${name}`}>
                              {String(row[name] ?? "")}
                            </Table.Cell>
                          ))}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>

                {parsedData.length > 10 && (
                  <Text fontSize="xs" color="gray.500">
                    ※ 처음 10개 행만 미리보기로 표시됩니다.
                  </Text>
                )}

                {uploading && (
                  <Box w="full">
                    <Text fontSize="xs" color="gray.600" mt={2}>
                      업로드 중... {progress}%
                    </Text>
                    <Box mt={1}>
                      <ProgressBar value={progress} max={100} style={{ width: "100%" }} />
                    </Box>
                  </Box>
                )}

              </>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <ButtonGroup>
            <DialogActionTrigger asChild>
              <Button variant="subtle" colorPalette="gray" disabled={uploading}>
                취소
              </Button>
            </DialogActionTrigger>
            <Button
              onClick={handleUpload}
              colorScheme="blue"
              loading={uploading}
              disabled={parsedData.length === 0 || uploading}
            >
              업로드
            </Button>
          </ButtonGroup>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

export default BulkUploadTableRows
